import type { CustomIconComponentProps } from '@ant-design/icons/lib/components/Icon';
import { isEmpty, isObject } from 'lodash';

import type { AvatarModelInterface } from '@/types/avatarModel';

export type IconType =
  | React.ComponentType<CustomIconComponentProps | React.SVGProps<SVGSVGElement>>
  | React.ForwardRefExoticComponent<CustomIconComponentProps>
  | undefined;

export function intersection(left: number[] | string, right: number[] | string) {
  const shortLength = left.length > right.length ? right : left;
  const longLength = left.length > right.length ? left : right;

  const result = [];

  for (let i = 0; i < shortLength.length; i++) {
    if (shortLength[i] === longLength[i]) {
      result.push(shortLength[i]);
    } else {
      break;
    }
  }

  return result;
}

export const parseMessageId = (msgId: string) => {
  if (msgId.length === 0) {
    return '';
  }

  const splitList = msgId.split('_____');

  if (splitList.length > 1) {
    return splitList[1];
  }

  return '';
};

export const isUrl = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  try {
    new URL(url);

    return true;
  } catch (err) {
    return false;
  }
};

export const isVoiceUrl = (url: string): boolean => {
  return (
    url.endsWith('.mp3') || url.endsWith('.wav') || url.endsWith('.m4a') || url.endsWith('.mp4')
  );
};

export const shortKey = (key: string) => {
  return key.slice(0, 3) + '***' + key.slice(-4);
};

export const sameAvatar = (left: AvatarModelInterface, right: AvatarModelInterface) => {
  if (left.model != null || right.model != null) {
    return left.avatar === right.avatar && left.gender == right.gender && left.model == right.model;
  }

  const isSameAvatar = left.avatar === right.avatar && left.type === right.type;

  return isSameAvatar;
};

const manualCopyFallback = (textToCopy: string): void => {
  const textArea = document.createElement('textarea');

  textArea.value = textToCopy;

  textArea.style.position = 'absolute';
  textArea.style.left = '-999999px';

  document.body.prepend(textArea);
  textArea.select();

  try {
    document.execCommand('copy');
  } catch (error) {
    console.error(error);
  } finally {
    textArea.remove();
  }
};

// 复制文本到剪贴板
export const copyToClipboard = (textToCopy: string) => {
  return new Promise<void>((resolve, reject) => {
    if (navigator.clipboard && window.isSecureContext) {
      setTimeout(() => {
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => {
            resolve();
          })
          .catch(() => {
            try {
              manualCopyFallback(textToCopy);
              resolve();
            } catch (manualError) {
              reject(manualError);
            }
          });
      });
    } else {
      try {
        manualCopyFallback(textToCopy);
        resolve();
      } catch {
        reject();
      }
    }
  });
};

export const downloadImage = (url: string, filename: string) => {
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const imageTypeList = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg', 'image/svg+xml'];

export const isExceedContentLimit = (content: string, maxLength: number): boolean => {
  // chinese character is 3 bytes
  // english character is 1 byte
  const length =
    content.length + (content.length - content.replace(/[\u4e00-\u9fa5]/g, '').length) * 2;

  if (length > maxLength) {
    return true;
  }

  return false;
};

export function isInChat(pathname: string = ''): boolean {
  // chat 页面不展示
  const lastPathIndex = pathname.lastIndexOf('/');
  const lastPath = pathname.substring(lastPathIndex);

  return lastPath === '/chat';
}

export const isValidHttpUrl = (string: string): boolean => {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
};

export const cutString = (str: string | undefined, len: number): string => {
  if (!str) {
    return '';
  }

  if (str.length > len) {
    return str.slice(0, len) + '...';
  }

  return str;
};

export interface SimpleRichTextSegment {
  start: number;
  end: number;
  isStrong?: boolean;
}

/**
 * 支持**text**方式加粗展示
 * @param text
 */
export const getSimpleRichTextSegments = (text: string) => {
  const matches = text.match(/\*\*([^*]*)\*\*/g);
  const segments: SimpleRichTextSegment[] = [];

  matches?.forEach((item) => {
    const startIndex = text.indexOf(item);
    const endIndex = startIndex + item.length;

    const lastSegment = segments[segments.length - 1];

    segments.push({
      start: lastSegment ? lastSegment.end : 0,
      end: startIndex
    });

    segments.push({
      start: startIndex,
      end: endIndex,
      isStrong: true
    });
  });

  if (!segments.length) {
    segments.push({
      start: 0,
      end: text.length
    });
  } else {
    const lastSegmentEndIndex = segments[segments.length - 1].end;

    if (lastSegmentEndIndex !== text.length - 1) {
      segments.push({
        start: lastSegmentEndIndex,
        end: text.length
      });
    }
  }

  return segments;
};

export function isCustomEmpty(obj: Record<string, any>) {
  if (isObject(obj)) {
    return Object.values(obj).every((value) => value === '' || value === undefined);
  }

  return isEmpty(obj);
}

export function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return count === 1 || count === 0 ? singular : plural;
}

/**
 * Utility function to handle pluralization in a sentence.
 *
 * @param {number} count - The number of items.
 * @param {string} singular - The singular form of the unit.
 * @param {string} plural - The plural form of the unit (optional).
 * @returns {string} The singular or plural form with the count, if includeCount is true.
 */
export function pluralizeWithCount(count: number, singular: string, plural = `${singular}s`) {
  const word = pluralize(count, singular, plural);

  return `${count} ${word}`;
}

export function pluralizeWithCountTName(count: number, key: string) {
  return count > 1 ? `${key}#` : key;
}

export function getMainDomain(url?: string) {
  if (!url) return url;

  try {
    const urlObj = new URL(url);

    return urlObj.hostname;
  } catch (error) {
    console.error('Invalid URL:', error);

    return url;
  }
}

export function isSafari() {
  const userAgent = navigator.userAgent;

  // 检查是否是 Safari
  const _isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

  return _isSafari;
}

export function isApple() {
  if (typeof navigator === 'undefined') return false;

  return (
    navigator.userAgent.toLowerCase().includes('macintosh') ||
    navigator.userAgent.toLowerCase().includes('iphone') ||
    navigator.userAgent.toLowerCase().includes('ipad') ||
    navigator.userAgent.toLowerCase().includes('ipod')
  );
}

export function isAndroid() {
  return navigator.userAgent.toLowerCase().includes('android');
}

export function isWeChat() {
  return navigator.userAgent.toLowerCase().includes('micromessenger');
}

export function isAndroidWechat() {
  return (
    navigator.userAgent.toLowerCase().includes('android') &&
    navigator.userAgent.toLowerCase().includes('micromessenger')
  );
}

/**
 * Check if the user is using a problematic Android device's built-in browser
 * These devices hijack video playback, so we need to show only cover image
 * Known devices: Honor 70, Honor Magic 7 Pro
 * Note: Chrome browser on these devices works fine
 */
export function isProblematicAndroidBrowser() {
  const ua = navigator.userAgent.toLowerCase();

  // If not Android, it's fine
  if (!ua.includes('android')) {
    return false;
  }

  // Check for Honor built-in browser (bdhonorbrowser)
  if (ua.includes('bdhonorbrowser')) {
    return true;
  }

  // If using standard Chrome browser (not built-in WebView), it's fine
  // Built-in browsers usually have "Version/4.0" in UA
  const isStandardChrome = ua.includes('chrome') && !ua.includes('version/4.0');

  if (isStandardChrome) {
    return false;
  }

  // Check for problematic device models
  const problematicModels = [
    'any-an00', // Honor 70 model code
    'ptp-an10' // Honor Magic 7 Pro model code
  ];

  return problematicModels.some((model) => ua.includes(model));
}

export const sleep = (delay = 500) => {
  const tt = Date.now();

  while (Date.now() - tt <= delay) {
    continue;
  }
};

export function imageUrlProcessing(imgUrl?: string): string {
  try {
    if (
      imgUrl &&
      ['object.me.bot', 'r2object.mindverse.com'].includes(new URL(imgUrl).hostname) &&
      !imgUrl.includes('?')
    ) {
      return imgUrl + '?mebot=true';
    }

    return imgUrl || '';
  } catch (e) {
    // 万一遇到奇怪地址，不要抛
    // logger.warn({ rid: getRid(), imgUrl }, 'imageUrlProcessing.bad_url');

    return imgUrl || '';
  }
}
